/**
 * TourCompletionExperience
 *
 * Full-screen celebration shown when a traveler reaches the final stop of a
 * tour. Renders confetti, an animated progress bar, a rich stats card, the
 * earned badges, and a prominent Share section. The Share section reuses
 * ShareWalkCard (already built) but the celebration wrapper is new.
 *
 * Props:
 *   tour                  — { title, city, cover_image_url }
 *   user                  — { name, email, first_name }
 *   sessionDurationSec    — wall-clock time from tour_started to now
 *   walkedDistanceMeters  — GPS-accumulated distance walked (preferred)
 *   walkedDurationSeconds — elapsed walking time from first GPS fix (preferred)
 *   stops                 — array of visible stops actually played
 *   stridesEarned         — number of Strides earned in this session
 *   newlyUnlockedAchievements — from the strides API
 *   selectedVersion       — 'full' | 'express'
 *   tourVersion           — 'full_access' etc.
 *   purchase              — { tour_id }
 *   tourUrl               — public tour URL for sharing
 *   token                 — auth token
 *   apiBase               — API base URL
 *   onShareClick          — invoked when user taps the big Share button
 *   onBackToTours         — invoked for "Back to My Tours"
 *   onExploreMore         — invoked for "Explore More Tours"
 *   NewsletterSlot        — optional inline node (existing newsletter block)
 *
 * The component is intentionally presentational — no direct auth/routing.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, MapPin, Clock, Footprints, Gauge, Award, Calendar } from 'lucide-react';
import axios from 'axios';
import { formatWalkedDistance, formatWalkedDuration, usesMilesLocale } from '../lib/walkingStats';

const formatDuration = (seconds) => {
  if (!seconds || seconds < 1) return '—';
  return formatWalkedDuration(seconds);
};

const formatDistance = (meters) => {
  if (!meters) return '—';
  return formatWalkedDistance(meters);
};

const formatDate = (iso) => {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return '';
  }
};

/**
 * Compute walking distance from an ordered array of stops with lat/lng.
 * Falls back to 0 for tours without GPS.
 */
const computeDistanceMeters = (stops) => {
  if (!stops || stops.length < 2) return 0;
  const R = 6371000;
  let total = 0;
  const gpsStops = stops.filter((s) => s && s.latitude && s.longitude && !s.is_intro);
  for (let i = 1; i < gpsStops.length; i++) {
    const a = gpsStops[i - 1];
    const b = gpsStops[i];
    const lat1 = parseFloat(a.latitude);
    const lat2 = parseFloat(b.latitude);
    const lon1 = parseFloat(a.longitude);
    const lon2 = parseFloat(b.longitude);
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    total += 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }
  return total;
};

const StatCell = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-teal-300" />
    </div>
    <div>
      <div className="text-[0.65rem] uppercase tracking-widest text-teal-300/70 font-bold">{label}</div>
      <div className="text-white text-lg font-bold leading-tight">{value}</div>
    </div>
  </div>
);

const TourCompletionExperience = ({
  tour,
  user,
  sessionDurationSec,
  walkedDistanceMeters,
  walkedDurationSeconds,
  stops,
  stridesEarned,
  newlyUnlockedAchievements,
  selectedVersion,
  purchase,
  tourUrl,
  token,
  apiBase,
  onShareClick,
  onBackToTours,
  onExploreMore,
  NewsletterSlot,
}) => {
  const [progress, setProgress] = useState(0);
  const [completion, setCompletion] = useState(null);
  const [confettiFired, setConfettiFired] = useState(false);
  const recordedRef = useRef(false);

  // 1) Kick the progress bar to 100% on mount.
  useEffect(() => {
    const t = setTimeout(() => setProgress(100), 200);
    return () => clearTimeout(t);
  }, []);

  // 2) Fire confetti — lazy import so it doesn't bloat the initial bundle.
  useEffect(() => {
    if (confettiFired) return;
    let cancelled = false;
    import('canvas-confetti').then((mod) => {
      if (cancelled) return;
      const confetti = mod.default || mod;
      const end = Date.now() + 1600;
      const colors = ['#22d3ee', '#a78bfa', '#fbbf24', '#fb7185', '#34d399'];
      const frame = () => {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          startVelocity: 45,
          origin: { x: 0, y: 0.7 },
          colors,
          disableForReducedMotion: true,
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          startVelocity: 45,
          origin: { x: 1, y: 0.7 },
          colors,
          disableForReducedMotion: true,
        });
        if (Date.now() < end && !cancelled) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
      setConfettiFired(true);
    }).catch(() => { /* graceful degrade — no confetti, still celebratory */ });
    return () => { cancelled = true; };
  }, [confettiFired]);

  // 3) Record the completion doc on mount (idempotent server-side).
  useEffect(() => {
    if (recordedRef.current) return;
    recordedRef.current = true;
    const tourId = purchase?.tour_id || purchase?.tour?.tour_id;
    if (!tourId || !token) return;
    // Prefer live GPS-accumulated distance (real "walked" metres) over the
    // legacy point-to-point calc between stops. Fall back to point-to-point
    // if the browser never delivered a usable GPS fix.
    const distance = (walkedDistanceMeters && walkedDistanceMeters > 0)
      ? walkedDistanceMeters
      : computeDistanceMeters(stops || []);
    const duration = (walkedDurationSeconds && walkedDurationSeconds > 0)
      ? walkedDurationSeconds
      : Math.max(0, Math.round(sessionDurationSec || 0));
    const payload = {
      tour_id: tourId,
      tour_version: selectedVersion || 'full',
      stops_completed: (stops || []).filter((s) => !s.is_intro).length,
      total_stops: (stops || []).length,
      distance_meters: distance,
      duration_seconds: duration,
      session_id: purchase?.purchase_id || null,
    };
    axios.post(`${apiBase}/tour-completions`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      setCompletion(res.data?.completion || null);
    }).catch(() => {
      // Fail quietly — the celebration UI still works with local numbers.
    });
  }, [purchase, token, apiBase, stops, sessionDurationSec, selectedVersion, walkedDistanceMeters, walkedDurationSeconds]);

  // Derived values shown in the UI. Server's numbers win once they arrive,
  // but we prefer live GPS values over the legacy stop-to-stop fallback.
  const distanceM = completion?.distance_meters
    ?? (walkedDistanceMeters && walkedDistanceMeters > 0
      ? walkedDistanceMeters
      : computeDistanceMeters(stops || []));
  const durationS = completion?.duration_seconds
    ?? (walkedDurationSeconds && walkedDurationSeconds > 0
      ? walkedDurationSeconds
      : Math.max(0, Math.round(sessionDurationSec || 0)));
  const paceKmh = completion?.avg_pace_kmh ?? 0;
  const paceLabel = (() => {
    if (!paceKmh || paceKmh <= 0) return '—';
    if (usesMilesLocale()) return `${(paceKmh * 0.621371).toFixed(1)} mph`;
    return `${paceKmh.toFixed(1)} km/h`;
  })();
  const stopsCount = completion?.stops_completed ?? (stops || []).filter((s) => !s.is_intro).length;
  const badges = completion?.badges_earned || newlyUnlockedAchievements || [];

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 p-4 py-8"
      data-testid="tour-completion-experience"
    >
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/40 animate-[bounce_1.4s_ease-in-out_1]">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-1" data-testid="completion-title">
            Congratulations!
          </h1>
          <p className="text-teal-200 text-base leading-snug">
            You completed the <strong className="text-white">{tour?.title || 'tour'}</strong>.
          </p>
          <p className="text-teal-300/70 text-sm mt-1">Thank you for exploring with Audiostride.</p>
        </div>

        {/* Animated progress bar — 0 → 100% */}
        <div className="mb-6" aria-hidden="true">
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 transition-all duration-[1400ms] ease-out"
              style={{ width: `${progress}%` }}
              data-testid="completion-progress-bar"
            />
          </div>
          <div className="flex justify-between text-[0.65rem] uppercase tracking-wider text-teal-300/70 mt-1.5 font-bold">
            <span>Journey complete</span>
            <span>100%</span>
          </div>
        </div>

        {/* Stats card */}
        <section
          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 mb-5"
          data-testid="completion-stats-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-lg font-black tracking-tight">Your Adventure</h2>
            <span className="text-xs text-teal-300/70 font-mono">{formatDate(completion?.completed_at)}</span>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-3">
            <StatCell icon={Footprints} label="Stops" value={stopsCount} testid="stat-stops" />
            <StatCell icon={MapPin} label="Distance" value={formatDistance(distanceM)} testid="stat-distance" />
            <StatCell icon={Clock} label="Time" value={formatDuration(durationS)} testid="stat-time" />
            <StatCell
              icon={Gauge}
              label="Avg pace"
              value={paceLabel}
              testid="stat-pace"
            />
            <StatCell icon={Calendar} label="City" value={tour?.city || '—'} testid="stat-city" />
          </div>
        </section>

        {/* Badges */}
        {badges && badges.length > 0 && (
          <section
            className="bg-gradient-to-br from-amber-500/15 to-orange-500/10 backdrop-blur-md border border-amber-400/20 rounded-3xl p-5 mb-5"
            data-testid="completion-badges"
          >
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-amber-300" />
              <h3 className="text-white font-bold uppercase text-xs tracking-widest">Badges earned</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {badges.slice(0, 6).map((b, i) => (
                <span
                  key={b.id || b.achievement_id || i}
                  className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold border border-white/20"
                  data-testid={`badge-${b.id || b.achievement_id || i}`}
                >
                  {b.label || b.title || b.name || 'Badge'}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Newsletter slot (existing block passed in from the page) */}
        {NewsletterSlot ? <div className="mb-5">{NewsletterSlot}</div> : null}

        {/* Big share CTA */}
        <div className="space-y-3">
          <button
            onClick={onShareClick}
            data-testid="completion-share-button"
            className="w-full bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 hover:brightness-110 text-white py-4 rounded-xl font-black text-lg shadow-xl shadow-orange-500/30 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Share your adventure
            {stridesEarned > 0 ? <span className="text-xs opacity-90">· +{stridesEarned} Strides</span> : null}
          </button>
          <button
            onClick={onBackToTours}
            data-testid="back-to-tours-button"
            className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-semibold border border-white/20 transition-all active:scale-95"
          >
            Back to My Tours
          </button>
          <button
            onClick={() => (window.location.href = '/passport/me')}
            data-testid="view-passport-button"
            className="w-full bg-gradient-to-r from-amber-600 to-orange-700 hover:brightness-110 text-white py-3 rounded-xl font-black text-sm shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            📖 See your new passport stamp
          </button>
          <button
            onClick={onExploreMore}
            data-testid="explore-more-button"
            className="w-full text-teal-300 hover:text-white py-2 text-sm font-medium transition-colors"
          >
            Explore More Tours
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourCompletionExperience;
